/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines-per-function */
import { useState } from 'react';
import { Card, Icon, Radio, Table, Typography } from '@equinor/eds-core-react';
import { external_link as externalLink } from '@equinor/eds-icons';
import { useFetchScenarioTemplates } from '../../../hooks/useFetchScenarioTemplates';
import { useUpdateScenarioDetails } from '../../../hooks/useUpdateScenarioDetails';
import TooltipPopover from '../../../components/TooltipPopover';
import TemplateDetailsDialog from '../../../components/TemplateDetailsDialog';
import { CenteredLoader } from '../../../components/CenteredLoader/CenteredLoader';
import { EditableFieldHeader } from '../components/EditableFieldHeader/EditableFieldHeader';
import { ScenarioDetailsDialog } from '../components/ScenarioDetailsDialog/ScenarioDetailsDialog';
import * as Styled from './InputParameters.styled';
import { useErrorStore } from '../../../stores/ErrorStore';

Icon.add({ externalLink });

interface InputParametersProps {
  scenario: any;
  hideDescription?: boolean;
}

interface Variable {
  name: string;
  validators?: {
    max?: number;
    min?: number;
    allowedValues?: number[];
  };
  units?: string;
  type?: string;
  id: string;
  default?: number | string;
  value?: number | string;
  description?: string;
}

interface Section {
  name: string;
  id: string;
  variables: Variable[];
  options?: Array<{ text: string; value: string }>;
}

export const InputParameters = ({
  scenario,
  hideDescription,
}: InputParametersProps) => {
  const templatesQuery = useFetchScenarioTemplates();
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] =
    useState<boolean>(false);
  const [isNameDialogOpen, setIsNameDialogOpen] = useState<boolean>(false);
  const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] =
    useState<boolean>(false);
  const [nameInputValue, setNameInputValue] = useState<string>('');
  const [descriptionInputValue, setDescriptionInputValue] =
    useState<string>('');
  const { addError } = useErrorStore();
  const updateScenarioDetails = useUpdateScenarioDetails();

  if (!scenario) {
    return (
      <Styled.Container>
        <Typography variant="h4">No scenario data available</Typography>
      </Styled.Container>
    );
  }

  if (templatesQuery.isLoading) {
    return <CenteredLoader />;
  }

  if (templatesQuery.isError || !templatesQuery.data?.data?.data) {
    addError('Failed to load scenario templates. Please try again.');
    return null;
  }

  const templates = templatesQuery.data?.data?.data;
  const currentTemplate = templates.find(
    (t: any) => t.scenarioTemplateId === scenario.scenario_template_id,
  );

  if (!currentTemplate) {
    return (
      <Styled.Container>
        <Typography variant="h4">Template not found</Typography>
      </Styled.Container>
    );
  }

  // Parse scenario data to get user-entered values
  let scenarioValues: any = {};
  if (scenario.json_scenario_data) {
    try {
      scenarioValues =
        typeof scenario.json_scenario_data === 'string'
          ? JSON.parse(scenario.json_scenario_data)
          : scenario.json_scenario_data;
    } catch (error) {
      addError('Failed to parse scenario data. The data may be corrupted.');
    }
  }

  // Parse template structure
  let templateData: any;
  try {
    templateData = JSON.parse(currentTemplate.jsonData);
  } catch (error) {
    addError('Failed to parse template data. The template may be corrupted.');
    return null;
  }

  const addedBy = scenario.created_by || 'Unknown';
  const addedDate = scenario.created_at
    ? new Date(scenario.created_at).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'Unknown';

  const templateName = currentTemplate.name || 'Unknown';
  const sections: Section[] = templateData?.fields?.sections || [];

  // Helper function to get value from scenario data
  const getVariableValue = (section: Section, variable: Variable) => {
    // Try nested format: scenarioValues.fields.sections[].variables[].value
    const scenarioSection = scenarioValues?.fields?.sections?.find(
      (s: any) => s.id === section.id || s.name === section.name,
    );

    if (scenarioSection) {
      const scenarioVariable = scenarioSection.variables?.find(
        (v: any) => v.id === variable.id,
      );

      if (scenarioVariable?.value !== undefined) {
        return scenarioVariable.value;
      }
    }

    // Try flat format: scenarioValues[variable.id].value (ScenarioBuilder format)
    if (scenarioValues?.[variable.id]?.value !== undefined) {
      return scenarioValues[variable.id].value;
    }

    // Fallback to default value from template
    return variable.default ?? '-';
  };

  const renderParameterTable = (section: Section) => {
    return (
      <Styled.StyledParametersTable>
        {section.variables.map((variable: Variable, variableIndex: number) => {
          const minVal = variable.validators?.min;
          const maxVal = variable.validators?.max;
          const allowedValues = variable.validators?.allowedValues;
          const value = getVariableValue(section, variable);

          let rangeDisplay = '';
          if (allowedValues && allowedValues.length > 0) {
            rangeDisplay = allowedValues.join(' or ');
          } else if (minVal !== undefined && maxVal !== undefined) {
            rangeDisplay = `${minVal} - ${maxVal}`;
          } else if (minVal !== undefined) {
            rangeDisplay = `${minVal}`;
          }

          return (
            <Table.Row
              key={
                variable.id ||
                `${
                  section.id || section.name || 'section'
                }-variable-${variableIndex}`
              }
            >
              <Table.Cell>
                <Typography variant="body_short">{variable.name}</Typography>
                {rangeDisplay && (
                  <Styled.RangeText variant="meta">
                    {rangeDisplay}
                  </Styled.RangeText>
                )}
              </Table.Cell>
              <Table.Cell>
                <Typography variant="body_short">{value}</Typography>
              </Table.Cell>
              <Table.Cell>
                <Typography variant="body_short">
                  {variable.units || '-'}
                </Typography>
              </Table.Cell>
              <Table.Cell>
                {variable.description && (
                  <TooltipPopover
                    tooltipTitle={variable.name}
                    description={variable.description}
                  />
                )}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Styled.StyledParametersTable>
    );
  };

  const renderSedimentComposition = (section: Section) => {
    const variable = section.variables[0] as any;

    // Get selected value from scenario data
    const scenarioSection = scenarioValues?.fields?.sections?.find(
      (s: any) => s.id === section.id || s.name === section.name,
    );
    const scenarioVariable = scenarioSection?.variables?.[0];
    // Support both nested format and flat ScenarioBuilder format
    const selectedValue =
      scenarioVariable?.value ??
      scenarioValues?.[variable?.id]?.value ??
      variable?.default;

    const capitalize = (str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    return (
      <Styled.SedimentSection>
        <Styled.SectionTitle variant="h4">{section.name}</Styled.SectionTitle>
        <Styled.RadioGroup>
          {variable?.options?.map((option: any, optionIndex: number) => (
            <Radio
              key={
                option.value ||
                `${section.id || section.name}-option-${optionIndex}`
              }
              label={capitalize(option.text)}
              name="sediment-composition"
              value={option.value}
              checked={selectedValue === option.value}
              disabled
            />
          ))}
        </Styled.RadioGroup>
      </Styled.SedimentSection>
    );
  };

  return (
    <Styled.Container>
      {!hideDescription && (
        <>
          <Styled.StyledCard variant="default">
            <Card.Header>
              <Card.HeaderTitle>
                <EditableFieldHeader
                  title="Name"
                  onEdit={() => {
                    setNameInputValue(scenario.name || '');
                    setIsNameDialogOpen(true);
                  }}
                  rightMeta={
                    <Typography variant="overline">
                      Added by {addedBy} on {addedDate}
                    </Typography>
                  }
                />
              </Card.HeaderTitle>
            </Card.Header>
            <Card.Content>
              <Typography variant="body_short">
                {scenario.name || 'No name provided'}
              </Typography>
            </Card.Content>
          </Styled.StyledCard>
          <Styled.StyledCard variant="default">
            <Card.Header>
              <Card.HeaderTitle>
                <EditableFieldHeader
                  title="Description"
                  onEdit={() => {
                    setDescriptionInputValue(scenario.description || '');
                    setIsDescriptionDialogOpen(true);
                  }}
                />
              </Card.HeaderTitle>
            </Card.Header>
            <Card.Content>
              <Typography variant="body_short">
                {scenario.description || 'No description provided'}
              </Typography>
            </Card.Content>
          </Styled.StyledCard>
          <ScenarioDetailsDialog
            isOpen={isNameDialogOpen}
            onClose={() => setIsNameDialogOpen(false)}
            onSave={() => {
              updateScenarioDetails.mutate(
                {
                  scenarioId: scenario.scenario_id,
                  name: nameInputValue.trim(),
                },
                { onSuccess: () => setIsNameDialogOpen(false) },
              );
            }}
            value={nameInputValue}
            onChange={setNameInputValue}
            isSaving={updateScenarioDetails.isPending}
            field="name"
          />
          <ScenarioDetailsDialog
            isOpen={isDescriptionDialogOpen}
            onClose={() => setIsDescriptionDialogOpen(false)}
            onSave={() => {
              updateScenarioDetails.mutate(
                {
                  scenarioId: scenario.scenario_id,
                  description: descriptionInputValue.trim(),
                },
                { onSuccess: () => setIsDescriptionDialogOpen(false) },
              );
            }}
            value={descriptionInputValue}
            onChange={setDescriptionInputValue}
            isSaving={updateScenarioDetails.isPending}
            field="description"
          />
        </>
      )}
      <Styled.StyledCard variant="default">
        <Card.Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Card.HeaderTitle>
            <Typography variant="h3">Template</Typography>
          </Card.HeaderTitle>
          <TemplateDetailsDialog
            tooltipTitle={currentTemplate.name}
            description={currentTemplate.description}
            picture={templateData?.fields?.picture || ''}
            setIsOpen={setIsTemplateDialogOpen}
            isOpen={isTemplateDialogOpen}
          />
        </Card.Header>
        <Card.Content>
          <Styled.TemplateNameText variant="body_short">
            {templateName}
          </Styled.TemplateNameText>

          {sections.map((section: Section, sectionIndex: number) => {
            const sectionKey =
              section.id || section.name || `section-${sectionIndex}`;

            if (section.name === 'Sediment composition') {
              return (
                <div key={sectionKey}>{renderSedimentComposition(section)}</div>
              );
            } else {
              return (
                <div key={sectionKey}>
                  <Styled.SectionTitle variant="h4">
                    {section.name}
                  </Styled.SectionTitle>
                  {renderParameterTable(section)}
                </div>
              );
            }
          })}
        </Card.Content>
      </Styled.StyledCard>
    </Styled.Container>
  );
};
